import { Box, ButtonNew, CheckBox, Spacer, TextInput, TextNew } from '@jfs/ui-new'
import { FC, ReactElement, useEffect, useState } from 'react'
import { beneficiaryOtherFilters, otherFilters } from '../../data/models/Filters'
import { Routes } from '../../nav/routes'
import { SnakeCase } from '../../util/util'
import { ModBox } from '../ModifiedBox/ModifiedBox'
import { RadioButtonGroup } from '../RadioButtonGroup'
import { FilterTextFields } from './FilterTextFields'
import { checkBeneficiaryInputAndShowError, checkInputAndShowError } from './FilterValidation'
import { MidSearchFilter } from './MidSearchFilter'
import { styles } from './styles'
import { SubscriptionFilters } from './SubscriptionFilters'
import { CLFilterProps, FilterItemProps, FilterProps, filterTypes } from './types'

export const CLFilter: FC<CLFilterProps> = ({
  filterData,
  updateFilters,
  style,
  selectedValue,
  mode,
}: CLFilterProps) => {
  const onClFilterChange = (data: any) => {
    updateFilters(mode, data)
  }

  return (
    <Box {...style}>
      <RadioButtonGroup
        size={12}
        data={filterData[mode]}
        direction={'vertical'}
        onChange={onClFilterChange}
        color={'BLUE'}
        textSize={12}
        value={selectedValue}
        textColor="PRIMARY_GREY"
        fontFamily={'jiotype-medium'}
      />
    </Box>
  )
}

export const FilterItem: FC<FilterItemProps> = ({
  hide,
  header,
  mode,
  updateFilters,
  filterData,
  checkSelected,
  selectedValue,
}: FilterItemProps) => {
  const themedStyle = styles()
  if (hide) {
    return null
  }
  const filtersObj = filterData[mode]
  const keys = Object.keys(filtersObj)
  const filterChild = keys?.map((element: any) => {
    const handleFilter = (): void => {
      updateFilters(mode, element)
    }
    return (
      <Box onPress={handleFilter} testID="handleFilter" {...themedStyle.childContainer} key={element}>
        <CheckBox
          checked={checkSelected(mode, element)}
          onChange={handleFilter}
          iconSize={16}
          color={checkSelected(mode, element) ? 'BLUE' : 'BLACK_LIGHT'}
        />
        <Spacer size={8} direction="horizontal" />
        <TextNew type="body12" color="PRIMARY_GREY" fontFamily="jiotype-medium">
          {filtersObj[element] ?? SnakeCase(element)}
        </TextNew>
      </Box>
    )
  })

  return (
    <Box {...themedStyle.innerContainer} key={header}>
      <TextNew type="body14" color="BLACK_21" fontFamily="jiotype-medium">
        {header}
      </TextNew>
      <Spacer size={12} direction="vertical" />
      <Box>{mode !== filterTypes.paymentLinkStatus && filterChild}</Box>
      {mode === filterTypes.paymentLinkStatus && (
        <CLFilter
          mode={filterTypes.paymentLinkStatus}
          filterData={filterData}
          updateFilters={updateFilters}
          style={themedStyle.clChildContainer}
          selectedValue={selectedValue}
        />
      )}
    </Box>
  )
}

export const Filters: FC<FilterProps> = ({
  onPress,
  updateMin,
  updateMax,
  selectedFilteredData,
  updateFilters,
  filterData,
  filterHeader,
  checkSelected,
  filtersStrings,
  updateFilterSettings,
  apply,
  path,
  updateOtherFilters,
  clearSubscriptionFilters,
  subMids,
  showMidFilter = false,
}: FilterProps): ReactElement => {
  const [initialState, setInitialState] = useState(0)
  const [initialData, setInitialData] = useState<any>({})
  const [invoiceError, setInvoiceError] = useState('')
  const [amountError, setAmountError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [merchantLinkError, setMerchantLinkError] = useState('')
  const [paymentLinkError, setPaymentLinkError] = useState('')
  const [contactError, setContactError] = useState('')

  const [beneficiaryTypeError, setBeneficiaryTypeError] = useState('')
  const [beneficiaryEmailError, setBeneficiaryEmailError] = useState('')
  const [beneficiaryNameError, setBeneficiaryNameError] = useState('')
  const [beneficiaryIdError, setBeneficiaryIdError] = useState('')
  const [beneficiaryPhoneError, setBeneficiaryPhoneError] = useState('')

  const [beneficiaryIfscError, setBeneficiaryIfscError] = useState('')
  const [beneficiaryUpiVpaError, setBeneficiaryUpiVpaError] = useState('')
  const [beneficiaryAccNoError, setBeneficiaryAccNoError] = useState('')
  const [beneficiaryCardError, setBeneficiaryCardError] = useState('')

  useEffect(() => {
    if (initialState < 1) {
      setInitialData(selectedFilteredData)
    }
  }, [initialState, selectedFilteredData])

  const themedStyle = styles()
  const updateHandler = (mode: filterTypes, value: string): void => {
    setInitialState((prev: number): number => prev + 1)
    updateFilters(mode, value)
  }

  const handleCancel = (): void => {
    updateFilterSettings(initialData)
    onPress()
  }

  const restoreDefault = () => {
    if (path === Routes.SUBSCRIPTIONS) {
      clearSubscriptionFilters()
      return
    }
    updateFilterSettings({ paymentLinkStatus: 'ALL' })
  }

  const handleApply = (): void => {
    if (path === Routes.LINKS) {
      if (!checkInputAndShowError()) {
        apply()
        // updateFilterSettings(initialData)
      }
    } else {
      apply()
    }
  }

  const handleMin = (value: string): void => {
    setInitialState((prev: number): number => prev + 1)
    updateMin(value)
  }

  const handleMax = (value: string): void => {
    setInitialState((prev: number): number => prev + 1)
    updateMax(value)
  }

  const handleFilter = (value: string, type: string) => {
    setInitialState((prev: number): number => prev + 1)
    updateOtherFilters && updateOtherFilters(value, type)
    if (path === Routes.LINKS) {
      checkInputAndShowError(
        value,
        type,
        selectedFilteredData,
        setContactError,
        setEmailError,
        setAmountError,
        setInvoiceError,
        setMerchantLinkError,
        setPaymentLinkError,
      )
    }
    if (path === Routes.PAYOUTS_BENEFICIARY) {
      checkBeneficiaryInputAndShowError(
        value,
        type,
        selectedFilteredData,
        setBeneficiaryTypeError,
        setBeneficiaryEmailError,
        setBeneficiaryNameError,
        setBeneficiaryIdError,
        setBeneficiaryPhoneError,
        setBeneficiaryIfscError,
        setBeneficiaryAccNoError,
        setBeneficiaryCardError,
        setBeneficiaryUpiVpaError,
      )
    }
  }

  const getRestoreStyles: any = () => {
    return {
      ...themedStyle.bottomBar,
      justifyContent: path === Routes.LINKS || path === Routes.SUBSCRIPTIONS ? 'space-between' : 'flex-end',
    }
  }

  return (
    <Box {...themedStyle.outerContainer} key={filtersStrings.labelFilters}>
      <Box {...themedStyle.topBar}>
        <TextNew type="body18" color="BLACK_21" fontFamily="jiotype-medium">
          {filtersStrings.labelFilters}
        </TextNew>
        <TextNew type="body14" color="PRIMARY_GREY" fontFamily="jiotype-medium">
          {filtersStrings.subTitle}
        </TextNew>
      </Box>
      <ModBox show={path === Routes.CAMPAIGNS} {...themedStyle.container}>
        <FilterItem
          header={'Campaign Type'}
          filterData={filterData}
          updateFilters={updateHandler}
          mode={filterTypes.campaignType}
          checkSelected={checkSelected}
        />
        <FilterItem
          header={filterHeader[1]}
          filterData={filterData}
          updateFilters={updateHandler}
          mode={filterTypes.campaignStatus}
          checkSelected={checkSelected}
          selectedValue={selectedFilteredData?.paymentLinkStatus}
        />
      </ModBox>
      <ModBox show={path !== Routes.SUBSCRIPTIONS && path !== Routes.CAMPAIGNS} {...themedStyle.container}>
        <FilterItem
          hide={path === Routes.LINKS || path === Routes.PAYOUTS_BENEFICIARY}
          header={filterHeader[0]}
          filterData={filterData}
          updateFilters={updateHandler}
          mode={filterTypes.paymentMode}
          checkSelected={checkSelected}
        />
        <FilterItem
          hide={!(path === Routes.LINKS || path === Routes.TRANSACTIONS || path === Routes.VAULT_TOKENIZATION)}
          header={filterHeader[1]}
          filterData={filterData}
          updateFilters={updateHandler}
          mode={path === Routes.LINKS ? filterTypes.paymentLinkStatus : filterTypes.transactionStatus}
          checkSelected={checkSelected}
          selectedValue={selectedFilteredData?.paymentLinkStatus}
        />
        {path !== Routes.LINKS && path !== Routes.PAYOUTS_BENEFICIARY && (
          <Box {...[themedStyle.innerContainer]}>
            <TextNew type="body14" color="BLACK_21" fontFamily="jiotype-medium">
              {filtersStrings.labelAmountRange}
            </TextNew>
            <Spacer size={12} direction="vertical" />
            <Box {...[themedStyle.childContainer, themedStyle.bottomFix]}>
              <TextInput
                type="body1"
                value={selectedFilteredData?.min ?? filtersStrings.empty}
                placeholder={filtersStrings.labelMin}
                testID={filtersStrings.testIdMin}
                onChangeText={handleMin}
                onFocusBottomLineColor={'BLUE'}
              />
            </Box>
            <Spacer size={12} direction="vertical" />
            <Box {...[themedStyle.childContainer, themedStyle.bottomFix]}>
              <TextInput
                type="body1"
                value={selectedFilteredData?.max ?? filtersStrings.empty}
                placeholder={filtersStrings.labelMax}
                testID={filtersStrings.testIdMax}
                onChangeText={handleMax}
                onFocusBottomLineColor={'BLUE'}
              />
            </Box>
          </Box>
        )}
        {path === Routes.LINKS && (
          <Box {...[themedStyle.innerContainer]}>
            <TextNew type="body14" color="BLACK_21" fontFamily="jiotype-medium">
              {filtersStrings.otherFilters}
            </TextNew>
            <Spacer size={18} direction="vertical" />
            <Box flexDirection="row">
              <Box>
                {FilterTextFields(
                  themedStyle,
                  contactError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  otherFilters,
                  'contact',
                  filtersStrings.testIdContact,
                  'contact',
                )}
                <Spacer size={10} direction="vertical" />
                {FilterTextFields(
                  themedStyle,
                  emailError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  otherFilters,
                  'email',
                  filtersStrings.testIdEmail,
                  'email',
                )}
                <Spacer size={10} direction="vertical" />
                {FilterTextFields(
                  themedStyle,
                  paymentLinkError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  otherFilters,
                  'paymentLinkId',
                  filtersStrings.testIdJioLinkId,
                  'paymentLink',
                )}
              </Box>
              <Spacer size={40} direction="horizontal" />
              <Box>
                {FilterTextFields(
                  themedStyle,
                  merchantLinkError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  otherFilters,
                  'merchantLinkId',
                  filtersStrings.testIdMerchantLinkId,
                  'paymentLink',
                )}
                <Spacer size={10} direction="vertical" />
                {FilterTextFields(
                  themedStyle,
                  amountError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  otherFilters,
                  'amount',
                  filtersStrings.testIdAmount,
                  'paymentLink',
                )}
                <Spacer size={10} direction="vertical" />
                {FilterTextFields(
                  themedStyle,
                  invoiceError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  otherFilters,
                  'invoiceNumber',
                  filtersStrings.testIdInvoiceNumber,
                  'paymentLink',
                )}
              </Box>
            </Box>
          </Box>
        )}
        {path === Routes.PAYOUTS_BENEFICIARY && (
          <Box {...[themedStyle.innerContainer]}>
            <TextNew type="body14" color="BLACK_21" fontFamily="jiotype-medium">
              {filtersStrings.otherFilters}
            </TextNew>
            <Spacer size={18} direction="vertical" />
            <Box flexDirection="row">
              <Box>
                {FilterTextFields(
                  themedStyle,
                  beneficiaryTypeError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  beneficiaryOtherFilters,
                  'beneficiaryType',
                  'beneficiaryType',
                  'textInput',
                )}
                <Spacer size={10} direction="vertical" />
                {FilterTextFields(
                  themedStyle,
                  beneficiaryEmailError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  beneficiaryOtherFilters,
                  'beneficiaryEmail',
                  'beneficiaryEmail',
                  'textInput',
                )}
                <Spacer size={10} direction="vertical" />
                {FilterTextFields(
                  themedStyle,
                  beneficiaryNameError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  beneficiaryOtherFilters,
                  'beneficiaryName',
                  'beneficiaryName',
                  'textInput',
                )}
              </Box>
              <Spacer size={40} direction="horizontal" />
              <Box>
                {FilterTextFields(
                  themedStyle,
                  beneficiaryIdError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  beneficiaryOtherFilters,
                  'beneficiaryId',
                  'beneficiaryId',
                  'textInput',
                )}
                <Spacer size={10} direction="vertical" />
                {FilterTextFields(
                  themedStyle,
                  beneficiaryPhoneError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  beneficiaryOtherFilters,
                  'beneficiaryPhoneNumber',
                  'beneficiaryPhoneNumber',
                  'textInput',
                )}
                <Spacer size={10} direction="vertical" />
                {FilterTextFields(
                  themedStyle,
                  beneficiaryAccNoError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  beneficiaryOtherFilters,
                  'beneficiaryAccountNumber',
                  'beneficiaryAccountNumber',
                  'textInput',
                )}
              </Box>
              <Spacer size={40} direction="horizontal" />
              <Box>
                {FilterTextFields(
                  themedStyle,
                  beneficiaryIfscError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  beneficiaryOtherFilters,
                  'beneficiaryIfsc',
                  'beneficiaryIfsc',
                  'textInput',
                )}
                <Spacer size={10} direction="vertical" />
                {FilterTextFields(
                  themedStyle,
                  beneficiaryUpiVpaError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  beneficiaryOtherFilters,
                  'beneficiaryUpiVpa',
                  'beneficiaryUpiVpa',
                  'textInput',
                )}
                <Spacer size={10} direction="vertical" />
                {FilterTextFields(
                  themedStyle,
                  beneficiaryCardError,
                  selectedFilteredData,
                  filtersStrings,
                  handleFilter,
                  beneficiaryOtherFilters,
                  'beneficiaryCardNumber',
                  'beneficiaryCardNumber',
                  'textInput',
                )}
              </Box>
            </Box>
          </Box>
        )}
      </ModBox>
      <ModBox show={path === Routes.SUBSCRIPTIONS}>
        <SubscriptionFilters
          checkSelected={checkSelected}
          updateFilters={updateHandler}
          selectedFilteredData={selectedFilteredData}
          updateOtherFilters={handleFilter}
        />
      </ModBox>
      {showMidFilter && (
        <MidSearchFilter subMids={subMids} updateFilters={updateHandler} checkSelected={checkSelected} />
      )}
      <Box {...getRestoreStyles()}>
        {(path === Routes.LINKS || path === Routes.SUBSCRIPTIONS) && (
          <ButtonNew
            title={path === Routes.LINKS ? filtersStrings.restoreDefault : filtersStrings.clearAll}
            {...themedStyle.restoreDefault}
            onPress={restoreDefault}
          />
        )}
        <Box {...themedStyle.filterHeader}>
          <ButtonNew
            title={filtersStrings.labelCancel}
            {...themedStyle.cancel}
            testID="close-filter"
            onPress={handleCancel}
          />
          <Spacer size={16} direction="horizontal" />
          <ButtonNew
            title={filtersStrings.labelApply}
            {...themedStyle.apply}
            testID="apply-filter"
            onPress={handleApply}
          />
        </Box>
      </Box>
    </Box>
  )
}
